import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  deleteSavedSearch,
  listSavedSearches,
  renameSavedSearch,
  slugify,
  writeSavedSearch,
  type SavedSearch,
  type SavedSearchScope,
  type SavedSearchSets,
} from '@/lib/saved-searches'
import { keys } from '@/lib/query'
import { useMe } from './use-me'

/** Org searches (shared) + the signed-in user's own, read from the work repo's
 *  app-data branch. Keyed by login so switching users doesn't show stale data. */
export function useSavedSearches() {
  const me = useMe()
  return useQuery<SavedSearchSets>({
    queryKey: keys.savedSearchesFor(me?.login ?? ''),
    enabled: !!me,
    staleTime: 5 * 60 * 1000,
    queryFn: () => listSavedSearches(me!.login),
  })
}

export type SaveSearchInput = {
  name: string
  filter: string
  scope: SavedSearchScope
  /** Reuse an existing slug to overwrite in place; otherwise derived from name. */
  slug?: string
}

/** Pick a slug that doesn't collide with an existing search in the same scope,
 *  so a new save never silently overwrites another (slugify is lossy). */
function freshSlug(base: string, scope: SavedSearchScope, sets: SavedSearchSets | undefined): string {
  const taken = new Set((scope === 'org' ? sets?.org : sets?.mine)?.map((s) => s.slug) ?? [])
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}-${n}`)) n++
  return `${base}-${n}`
}

export function useSaveSearch() {
  const me = useMe()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveSearchInput): Promise<SavedSearch> => {
      if (!me) throw new Error('not signed in')
      // Explicit slug = deliberate overwrite (edit). Otherwise derive a fresh,
      // non-colliding slug from the cached list.
      const slug =
        input.slug ??
        freshSlug(slugify(input.name), input.scope, qc.getQueryData<SavedSearchSets>(keys.savedSearchesFor(me.login)))
      const search: SavedSearch = {
        name: input.name,
        filter: input.filter,
        scope: input.scope,
        owner: input.scope === 'user' ? me.login : null,
        slug,
        createdBy: me.login,
        createdAt: new Date().toISOString(),
      }
      await writeSavedSearch(search)
      return search
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.savedSearches }),
  })
}

export function useRenameSavedSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ search, name }: { search: SavedSearch; name: string }) =>
      renameSavedSearch(search, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.savedSearches }),
  })
}

export function useDeleteSavedSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (search: SavedSearch) => deleteSavedSearch(search),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.savedSearches }),
  })
}
